import './ShopCategoryModal.css';
import React, { useMemo } from 'react';
import { Button } from '../../../../../../components/Button/Button';
import { Icon } from '../../../../../../components/Icon/Icon';
import {
  addCategoryAssignment,
  patchCategoryAssignment,
  deleteCategoryAssignment,
  CategoryType,
  Category,
  categoryTypes,
  OrderedCategory,
} from '../../../../../../model/category';
import { t } from '../../../../../../locales/translate';
import { Event } from '../../../../../../model/event';
import { ProductCategory, Product } from '../../../../../../model/product';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { FormNumberField } from '../../../../../../components/Form/NumberField/FormNumberField';
import { compact } from 'lodash';
import { SubmitLine } from '../../../../../../components/SubmitLine/SubmitLine';
import { Stack } from '../../../../../../components/Stack/Stack';
import { Spacer } from '../../../../../../components/Spacer/Spacer';
import { FormSearch } from '../../../../../../components/Form/AsyncSearch/FormSearch';
import { uniqueId } from 'lodash';
import { MappedFields } from '@nerdgeschoss/react-use-form-library/dist/field';

interface Props {
  product: Event | Product;
  blacklist?: CategoryType[];
  addCategoryTitle?: string;
  onChange?: () => void | Promise<void>;
  onClose: () => void;
}

interface CategoryModel {
  id?: string;
  rowId?: string;
  order: number | null;
  type?: string | null;
}

function oneLevelDiff(
  obj: OrderedCategory[],
  obj2?: CategoryModel[]
): {
  added: CategoryModel[];
  edited: CategoryModel[];
  deleted: string[];
} {
  const added = obj2?.filter((item) => !obj.find((item2) => item.id === item2.category.id));
  const edited = obj2?.filter((item2) =>
    obj.find((item) => item.category.id === item2.id && item.order !== item2.order)
  );
  const deleted: string[] = obj
    .filter((item) => {
      if (!obj2?.find((item2) => item.category.id === item2.id)) {
        return true;
      }
      return false;
    })
    .map((item) => item.category.id);

  return {
    added: added || [],
    edited: edited || [],
    deleted,
  };
}

function filterFieldsByType(
  fields: MappedFields<{ categories: CategoryModel[] }>,
  type: CategoryType
): { fields: MappedFields<CategoryModel>; remove: () => void }[] {
  return fields.categories.elements.filter((field) => {
    const categoryType = field.fields.type.value;

    if (!categoryType && type === 'category') {
      return true;
    }

    return type === categoryType;
  });
}

export function ShopCategoryModal({ product, blacklist, onChange, onClose, addCategoryTitle }: Props): JSX.Element {
  const productCategories = product.categories as OrderedCategory[];

  const memoProductCategories = useMemo(
    () =>
      productCategories.map((item) => ({
        rowId: uniqueId('category_'),
        id: item.category.id,
        order: item.order,
        type: item.category.type,
      })),
    [productCategories]
  );

  const { model, fields, onSubmit, changes, submissionStatus, dirty } = useForm<{
    categories: CategoryModel[];
  }>({
    model: {
      categories: memoProductCategories,
    },
    onSubmit: async () => {
      const diff = oneLevelDiff(productCategories, changes.categories);

      const promises: Promise<ProductCategory | void>[] = [];

      diff.added.forEach(async (add) => {
        if (add.id) {
          promises.push(addCategoryAssignment({ productId: product.id, categoryId: add.id, order: add.order }));
        }
      });

      diff.edited.forEach((edit) => {
        if (edit.id) {
          promises.push(patchCategoryAssignment({ productId: product.id, categoryId: edit.id, order: edit.order }));
        }
      });

      diff.deleted.forEach((del) => {
        promises.push(deleteCategoryAssignment(product.id, del));
      });

      await Promise.all(promises);
      await onChange?.();
      onClose();
    },
  });

  const filteredCategories = categoryTypes.map((type) => {
    return {
      title: t.model[type]._plural || type,
      fields: filterFieldsByType(fields, type),
    };
  });

  function canSubmit(): boolean {
    const diff = oneLevelDiff(productCategories, changes.categories);
    if (dirty && diff.deleted.length) return true;
    if (!changes.categories?.length) return false;

    return !!(diff.added.length || diff.edited.length);
  }

  return (
    <form>
      <div className="event-shop__categories-wrapper">
        {filteredCategories.map((group) => {
          if (!group.fields.length) return null;

          return (
            <div key={group.title}>
              <Stack key={group.title} size="sm">
                <span className="event-shop__categories-label">{group.title}</span>
                {group.fields.map((field) => {
                  return (
                    <ShopBar
                      key={field.fields.rowId.value}
                      model={field}
                      blacklist={blacklist}
                      handleRemove={() => field.remove()}
                      categories={product.categories as OrderedCategory[]}
                      currentCategories={model.categories.filter((item) => item.id)}
                    />
                  );
                })}
              </Stack>
              <Spacer />
            </div>
          );
        })}
      </div>
      <Button
        title={addCategoryTitle || t.actions.add(t.model.event.ShopCategories)}
        type="link"
        className="shop-category__add-tour-button"
        onClick={() =>
          fields.categories.add({
            order: 0,
            rowId: uniqueId('category_'),
          })
        }
        icon="plus"
      />
      <SubmitLine>
        <Button title={t.actions.cancel('')} onClick={() => onClose()} />
        <Button
          title={t.actions.save('')}
          type="primary"
          disabled={!canSubmit()}
          loading={submissionStatus === 'submitting'}
          onClick={() => onSubmit()}
        />
      </SubmitLine>
    </form>
  );
}

function ShopBar({
  model,
  currentCategories,
  categories,
  blacklist,
  handleRemove,
}: {
  currentCategories: CategoryModel[];
  blacklist?: CategoryType[];
  model: { fields: MappedFields<CategoryModel> };
  categories: OrderedCategory[];
  handleRemove: () => void;
}): JSX.Element {
  const categoryType = categories.find((cat) => cat.category.id === model.fields.id.value)?.category.type;

  return (
    <div className="shop-category__category-bar">
      <FormSearch
        label={categoryType ? t.model[categoryType]._name : t.general.new}
        required
        searchIn={['category']}
        additionalParams={
          blacklist?.length
            ? {
                categoryTypes: categoryTypes.filter((item) => !blacklist?.includes(item)),
              }
            : undefined
        }
        value={model.fields.id.value}
        onChange={(value) => model.fields.id.onChange(value)}
        onChangeObject={(value) => {
          setTimeout(() => model.fields.type.onChange((value as Category)?.type), 0);
        }}
        className="shop-category__tour-select"
        hiddenValues={compact(currentCategories.map((item) => item.id))}
        loadingMessage={`${t.model.category.loadingCategory}...`}
      />
      <FormNumberField
        value={model.fields.order.value || 0}
        label="Sort"
        className="shop-category__sort-order"
        onChange={(value) => {
          if (value !== undefined) model.fields.order.onChange(value);
        }}
      />
      <Icon name="remove-circle" className="shop-category__remove-icon" onClick={handleRemove} />
    </div>
  );
}
